import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import "react-phone-input-2/lib/bootstrap.css";
import { AGE_GROUP } from "../../utils/enums";
import { showError, showToast } from "../../constants/toasts";
import {
  useAddHudrationMutation,
  useLazyGetHudrationByIdQuery,
  useUpdateHudrationMutation,
} from "../../services/hydration&Goal";

const AddHydration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editIdParam = searchParams.get("id");
  const isEdit = useMemo(() => Boolean(editIdParam), [editIdParam]);

  const [ageGroup, setAgeGroup] = useState<string>("");
  const [dailyHydration, setDailyHydration] = useState<string>("");
  const [goalType, setGoalType] = useState<string>("");
  const [duration, setDuration] = useState<string>("");
  const [missedLimits, setMissedLimits] = useState<string>("");
  const [lighterGoalVersion, setLighterGoalVersion] = useState<string>("");
  const [smartSuggestion, setSmartSuggestion] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const MAX_LEN = 50;

  const [addHydration] = useAddHudrationMutation();
  const [updateHydration] = useUpdateHudrationMutation();
  const [getHydrationById] = useLazyGetHudrationByIdQuery();

  useEffect(() => {
    const init = async () => {
      if (isEdit && editIdParam) {
        try {
          const idNum = parseInt(editIdParam, 10);
          if (!idNum || Number.isNaN(idNum)) return;
          const res = await getHydrationById({ id: idNum }).unwrap();
          const data = res?.data || {};
          setAgeGroup(
            (data?.age_group || data?.ageGroup || data?.age || "").toString()
          );
          setDailyHydration(
            (
              data?.daily_hydration?.toString?.() ||
              data?.hydrationTarget?.toString?.() ||
              ""
            ).slice(0, MAX_LEN)
          );
          setGoalType(
            (data?.goal_type || data?.goalTemplate || "")
              .toString()
              .slice(0, MAX_LEN)
          );
          setDuration(
            (data?.duration?.toString?.() || "").toString().slice(0, MAX_LEN)
          );
          setMissedLimits(
            (
              data?.missed_limits?.toString?.() ||
              data?.missing_limit?.toString?.() ||
              data?.missedGoals?.toString?.() ||
              ""
            ).slice(0, MAX_LEN)
          );
          setLighterGoalVersion(
            (data?.lighter_goal_version || "").toString().slice(0, MAX_LEN)
          );
          setSmartSuggestion(
            (data?.smart_suggestion || data?.suggestion || "")
              .toString()
              .slice(0, MAX_LEN)
          );
        } catch (e: any) {
          showError(e?.data?.message || "Failed to load details");
        }
      } else {
        setAgeGroup("");
        setDailyHydration("");
        setGoalType("");
        setDuration("");
        setMissedLimits("");
        setLighterGoalVersion("");
        setSmartSuggestion("");
      }
    };
    init();
  }, [isEdit, editIdParam, getHydrationById]);

  const handleSubmit = async () => {
    setError(true);
    const trimmedAgeGroup = (ageGroup || "").trim();
    const trimmedDailyHydration = (dailyHydration || "").trim();
    const trimmedGoalType = (goalType || "").trim();
    const trimmedDuration = (duration || "").trim();
    const trimmedMissedLimits = (missedLimits || "").trim();
    const trimmedLighterGoalVersion = (lighterGoalVersion || "").trim();
    const trimmedSmartSuggestion = (smartSuggestion || "").trim();
    if (!trimmedAgeGroup) {
      showError("Age group is required");
      return;
    }
    if (!trimmedDailyHydration) {
      showError("Daily hydration is required");
      return;
    }
    if (!trimmedGoalType) {
      showError("Goal type is required");
      return;
    }
    if (!trimmedDuration) {
      showError("Duration is required");
      return;
    }
    if (!trimmedMissedLimits) {
      showError("Missed limits is required");
      return;
    }
    if (!trimmedLighterGoalVersion) {
      showError("Lighter goal version is required");
      return;
    }
    if (!trimmedSmartSuggestion) {
      showError("Smart suggestion is required");
      return;
    }

    if (
      trimmedDailyHydration.length > MAX_LEN ||
      trimmedGoalType.length > MAX_LEN ||
      trimmedDuration.length > MAX_LEN ||
      trimmedMissedLimits.length > MAX_LEN ||
      trimmedLighterGoalVersion.length > MAX_LEN ||
      trimmedSmartSuggestion.length > MAX_LEN
    ) {
      showError(`Max ${MAX_LEN} characters allowed`);
      return;
    }

    const body: any = {
      age: trimmedAgeGroup,
      daily_hydration: trimmedDailyHydration,
      goal_type: trimmedGoalType,
      duration: trimmedDuration,
      missing_limit: trimmedMissedLimits,
      lighter_goal_version: trimmedLighterGoalVersion,
      smart_suggestion: trimmedSmartSuggestion,
      ...(isEdit && editIdParam ? { id: Number(editIdParam) } : {}),
    };

    setIsLoading(true);
    try {
      let res: any;
      if (isEdit && editIdParam) {
        res = await updateHydration({ body, id: Number(editIdParam) }).unwrap();
      } else {
        res = await addHydration({ body }).unwrap();
      }
      if (res?.status === 200 || res?.status === 201) {
        showToast(
          isEdit
            ? "Hydration updated successfully"
            : "Hydration added successfully"
        );
        navigate("/hydration");
      }
    } catch (e: any) {
      showError(e?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>{isEdit ? "Edit" : "Add"} Hydration & Goal Settings</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/hydration")}>
            Hydration & Goal Settings
          </span>{" "}
          - {isEdit ? "Edit" : "Add"} Hydration
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_33">
              <label>Age Group</label>
              <Select
                fullWidth
                value={ageGroup || ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setAgeGroup(e.target.value as string)
                }
                displayEmpty
                renderValue={(selected) =>
                  selected && String(selected).length
                    ? String(selected)
                    : "Select"
                }
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                {AGE_GROUP.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {error && !ageGroup ? (
                <h6 className="err_msg">Age group is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Daily Hydration(In Liters)</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                type="text"
                value={dailyHydration}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setDailyHydration(e.target.value)}
              />
              {error && !dailyHydration.trim() ? (
                <h6 className="err_msg">Daily hydration is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Goal Type</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                value={goalType}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setGoalType(e.target.value)}
              />
              {error && !goalType.trim() ? (
                <h6 className="err_msg">Goal type is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Duration</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                value={duration}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setDuration(e.target.value)}
              />
              {error && !duration.trim() ? (
                <h6 className="err_msg">Duration is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Missed Limits</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                type="text"
                value={missedLimits}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setMissedLimits(e.target.value)}
              />
              {error && !missedLimits.trim() ? (
                <h6 className="err_msg">Missed limits is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Lighter Goal Version</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                value={lighterGoalVersion}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setLighterGoalVersion(e.target.value)}
              />
              {error && !lighterGoalVersion.trim() ? (
                <h6 className="err_msg">Lighter goal version is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Smart Suggestion</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                value={smartSuggestion}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setSmartSuggestion(e.target.value)}
              />
              {error && !smartSuggestion.trim() ? (
                <h6 className="err_msg">Smart suggestion is required</h6>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isEdit
                ? isLoading
                  ? "Updating..."
                  : "Update"
                : isLoading
                ? "Saving..."
                : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddHydration;
